import { useMemo, useCallback, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { CustomInvestmentMetadata, HoldingsBenchmarkCategory, Portfolio, Fund, PortfolioCompare } from 'venn-api';
import { getCustomFundMetadata, getHoldingsCategories } from 'venn-api';
import { createCustomFundMetadataKey, createFixedStrategiesKey, useHasFF } from 'venn-utils';
import { cloneDeep, isEmpty, partition, sortBy, minBy, last } from 'lodash';
import type { PortfolioParseResultWithReviewMetadata } from '../views/components/common/MultiPortfolioReviewContext';
import { remapExcludedPathKeys } from '../utils/portfolioTree';
import {
  buildStrategyPathNames,
  findStrategyPath,
  reassignInvestmentNode,
  stripStrategyNodes,
} from '../utils/strategyUtils';

export type StrategyAssignments = Record<string, CustomInvestmentMetadata>;

export type UseAssignedPortfoliosArgs = Readonly<{ parsedResults: PortfolioParseResultWithReviewMetadata[] }>;

export type UseAssignedPortfoliosReturn = Readonly<{
  assignedResults: PortfolioParseResultWithReviewMetadata[];
  loading: boolean;
  manualAssignments: Map<string, ManualAssignment>;
  assignInvestment: (index: number, fund: Fund, categoryId?: string, isManualAssignment?: boolean) => void;
}>;

type ManualAssignment = {
  fundId: string;
  categoryId: number;
  hierarchyLevels: string[];
};

export const useAssignedPortfolios = ({ parsedResults }: UseAssignedPortfoliosArgs): UseAssignedPortfoliosReturn => {
  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');

  // Collect all fund ids from all portfolios
  const fundIds = useMemo(() => {
    if (!hasManualStrategyAssignment) return [];
    const ids = new Set<string>();
    const collect = (node: Portfolio) => {
      if (node.fund?.id) ids.add(node.fund.id);
      node.children?.forEach(collect);
    };
    for (const r of parsedResults) collect(r.parsedPortfolio);
    return Array.from(ids);
  }, [parsedResults, hasManualStrategyAssignment]);

  // Fetch full taxonomy
  const { data: categories = [], isFetching: isLoadingStrategies } = useQuery<HoldingsBenchmarkCategory[]>(
    createFixedStrategiesKey('ASSET', 'WORKSPACE'),
    async () => (await getHoldingsCategories('ASSET', 'WORKSPACE')).content || [],
    { staleTime: 10 * 60 * 1000, keepPreviousData: true, enabled: hasManualStrategyAssignment },
  );

  // Fetch server assignments per fund id
  const { data: baseAssignments = {}, isFetching: isLoadingAssignments } = useQuery<StrategyAssignments>(
    createCustomFundMetadataKey(fundIds),
    async () => {
      if (!hasManualStrategyAssignment || fundIds.length === 0) return {} as StrategyAssignments;
      const { content } = await getCustomFundMetadata(fundIds);
      return content || {};
    },
    {
      enabled: hasManualStrategyAssignment && fundIds.length > 0,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );

  const categoryOrderingMap = useMemo(() => {
    if (!hasManualStrategyAssignment || categories.length === 0) return [];

    const flattenWithLevels = (
      cats: HoldingsBenchmarkCategory[],
      level = 0,
    ): { name: string; ordering: number; level: number }[] =>
      cats.flatMap((cat) => [
        ...(cat.categoryName ? [{ name: cat.categoryName, ordering: cat.ordering ?? Infinity, level }] : []),
        ...(cat.children ? flattenWithLevels(cat.children, level + 1) : []),
      ]);

    return flattenWithLevels(categories);
  }, [categories, hasManualStrategyAssignment]);

  const getOrderingForNode = useCallback(
    (nodeName: string): number => {
      const matchingCategories = categoryOrderingMap.filter((cat) => cat.name === nodeName);
      const highestLevelCategory = minBy(matchingCategories, 'level');

      return highestLevelCategory?.ordering ?? Infinity;
    },
    [categoryOrderingMap],
  );

  const sortPortfolioByOrdering = useCallback(
    (portfolio: Portfolio, currentDepth = 0): Portfolio => {
      if (!hasManualStrategyAssignment || categoryOrderingMap.length === 0) return portfolio;

      const sorted = { ...portfolio };
      if (!isEmpty(sorted.children)) {
        const recursiveSorted = sorted.children.map((child) => sortPortfolioByOrdering(child, currentDepth + 1));

        const [unassignedFunds, strategyNodes] = partition(recursiveSorted, (child) => child.fund || !child.name);

        // Sort strategy nodes by ordering, keep unassigned funds in original order at top
        const sortedStrategyNodes = sortBy(strategyNodes, (child) => getOrderingForNode(child.name));
        sorted.children = [...unassignedFunds, ...sortedStrategyNodes];
      }
      return sorted;
    },
    [hasManualStrategyAssignment, categoryOrderingMap, getOrderingForNode],
  );

  const [manualAssignments, setManualAssignments] = useState<Map<string, ManualAssignment>>(new Map());

  const applyStrategy = useCallback(
    (
      portfolio: Portfolio,
      fundId: string,
      hierarchyLevels: string[],
      excludedInvestments: Set<string>,
      benchmarkCategory: HoldingsBenchmarkCategory | undefined,
    ): { portfolio: Portfolio; excludedInvestments: Set<string> } => {
      const target = { type: 'fundId' as const, fundId };
      const newPortfolio = reassignInvestmentNode(portfolio, { target, hierarchyLevels });
      if (benchmarkCategory?.benchmarkFundUuid) {
        const newCompareEntry = {
          fundId: benchmarkCategory.benchmarkFundUuid,
          benchmark: true,
          historical: true,
          name: benchmarkCategory.categoryName,
          primary: true,
          readOnly: false,
          type: 'PORTFOLIO',
        } as PortfolioCompare;

        const existingCompare = Array.isArray(newPortfolio.compare) ? newPortfolio.compare : [];
        const existingFilteredCompare = existingCompare.filter((c) => c.fundId !== newCompareEntry.fundId);
        newPortfolio.compare = [...existingFilteredCompare, newCompareEntry];
      }
      const newExcludedInvestments = remapExcludedPathKeys(portfolio, newPortfolio, excludedInvestments);
      return { portfolio: newPortfolio, excludedInvestments: newExcludedInvestments };
    },
    [],
  );

  const assignedResults = useMemo<PortfolioParseResultWithReviewMetadata[]>(() => {
    if (!hasManualStrategyAssignment || categories.length === 0) return parsedResults;

    // Step 1: Apply base assignments from server
    const initialResults = parsedResults.map((r) => {
      const sourceRoot = r.parsedPortfolio;
      let nextRoot = stripStrategyNodes(cloneDeep(sourceRoot));
      for (const [fid, meta] of Object.entries(baseAssignments)) {
        const categoryId = meta?.assetCategoryId;
        if (!categoryId) continue;
        const hierarchyLevels = buildStrategyPathNames(categories, categoryId) ?? [];
        if (!hierarchyLevels.length) continue;
        nextRoot = reassignInvestmentNode(nextRoot, { target: { type: 'fundId', fundId: fid }, hierarchyLevels });
      }
      return {
        ...r,
        parsedPortfolio: nextRoot,
        excludedInvestments: remapExcludedPathKeys(sourceRoot, nextRoot, r.excludedInvestments),
      };
    });

    // Step 2: Apply manual assignments on top of initial results
    const mergedResults = [...initialResults];
    for (const [fundId, manualAssignment] of manualAssignments) {
      // Find and reapply the manual assignment for this fund
      const benchmarkCategory = last(findStrategyPath(categories, manualAssignment.categoryId));
      for (let i = 0; i < mergedResults.length; i++) {
        const result = mergedResults[i];
        if (!result) continue;

        const containsFund = (node: Portfolio): boolean => {
          if (node.fund?.id === fundId) return true;
          return (node.children || []).some(containsFund);
        };

        if (containsFund(result.parsedPortfolio)) {
          const { portfolio: newPortfolio, excludedInvestments: newExcludedInvestments } = applyStrategy(
            result.parsedPortfolio,
            fundId,
            manualAssignment.hierarchyLevels,
            result.excludedInvestments,
            benchmarkCategory,
          );

          mergedResults[i] = {
            ...result,
            parsedPortfolio: newPortfolio,
            excludedInvestments: newExcludedInvestments,
          };
        }
      }
    }

    // Step 3: Sort final results
    return mergedResults.map((result) => ({
      ...result,
      parsedPortfolio: sortPortfolioByOrdering(result.parsedPortfolio),
    }));
  }, [
    parsedResults,
    baseAssignments,
    hasManualStrategyAssignment,
    categories,
    manualAssignments,
    applyStrategy,
    sortPortfolioByOrdering,
  ]);

  const fetchCategoryAndAssign = useMutation({
    mutationFn: async (vars: { index: number; fund: Fund }) => {
      const { fund } = vars;
      const { content } = await getCustomFundMetadata([fund.id]);
      const metadata = content?.[fund.id];
      return { ...vars, categoryId: metadata?.assetCategoryId };
    },
  });

  const assignInvestment = useCallback(
    (index: number, fund: Fund, categoryId?: string, isManualAssignment = true) => {
      if (!hasManualStrategyAssignment) return;

      const applyCategoryAssignment = (categoryIdToApply: number, manualAssignment: boolean) => {
        const hierarchyLevels = buildStrategyPathNames(categories, categoryIdToApply) ?? [];
        if (!hierarchyLevels.length) return;

        if (manualAssignment && fund.id) {
          setManualAssignments((prev) =>
            new Map(prev).set(fund.id, {
              fundId: fund.id,
              categoryId: categoryIdToApply,
              hierarchyLevels,
            }),
          );
        }
      };

      if (categoryId) {
        const parsedCategoryId = parseInt(categoryId, 10);
        if (Number.isFinite(parsedCategoryId)) {
          applyCategoryAssignment(parsedCategoryId, isManualAssignment);
        }
      } else {
        // No category provided: fetch from fund metadata (automatic assignment via mapping)
        fetchCategoryAndAssign.mutate(
          { index, fund },
          {
            onSuccess: (result) => {
              if (result.categoryId) {
                applyCategoryAssignment(result.categoryId, false);
              }
            },
          },
        );
      }
    },
    [categories, hasManualStrategyAssignment, fetchCategoryAndAssign],
  );

  return {
    assignedResults,
    loading: isLoadingStrategies || isLoadingAssignments,
    manualAssignments,
    assignInvestment,
  };
};
